import type { PolicyViewModel } from 'src/api/types/PolicyViewModel';

import Grid from '@mui/material/Grid';
import { Box, Divider, Typography } from '@mui/material';

import {
  formatDate,
  formatDollarAmount,
  renderEmployeeLeasingTypeWithTooltip,
} from 'src/sections/employer-data/shared/utils/policyUtils';

import { InfoBox } from '../../shared/components/info-box';
import { CoverageReadOnlyFields, PolicyReadOnlyFields } from './policy-information-read-only-fields';
import { PolicyInformationInfoTable } from './policy-information-info-table';

type PolicyInformationViewProps = {
  comboId: number;
  viewPolicy: PolicyViewModel;
};

export function PolicyInformationView({ comboId, viewPolicy }: Readonly<PolicyInformationViewProps>) {
  return (
    <>
      <Box sx={{ padding: '1.5rem' }}>
        <Typography variant="h5" sx={{ color: 'text.primary' }} data-testid="coverage-profile-title">
          Coverage
        </Typography>

        <Grid container spacing={2} columns={16}>
          <CoverageReadOnlyFields comboId={comboId} viewPolicy={viewPolicy} />
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Interstate risk ID"
              value={viewPolicy?.interstateRiskNumber || ''}
              testIdLabel="interstate-risk-id-label"
              testIdValue="interstate-risk-id-value"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Legal nature"
              value={viewPolicy?.legalNatureEnumDescription || ''}
              testIdLabel="legal-nature-label"
              testIdValue="legal-nature-value"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            {renderEmployeeLeasingTypeWithTooltip(viewPolicy?.policyEmployeeLeasingPolicyType)}
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Retro rating"
              value={viewPolicy?.retroRatingTypeEnumDescription || ''}
              testIdLabel="retro-rating-label"
              testIdValue="retro-rating-value"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Minimum premium state"
              value={viewPolicy?.minimumPremiumState || ''}
              testIdLabel="minimum-premium-state-label"
              testIdValue="minimum-premium-state-value"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Market"
              value={viewPolicy?.planTypeEnumDescription || ''}
              testIdLabel="market-label"
              testIdValue="market-value"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ borderStyle: 'dashed', marginTop: '.25rem' }} />

      <Box sx={{ padding: '1.5rem' }}>
        <Typography variant="h5" sx={{ color: 'text.primary' }} data-testid="coverage-profile-title">
          Policy
        </Typography>

        <Grid container spacing={2} columns={16}>
          <PolicyReadOnlyFields viewPolicy={viewPolicy} />
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Agency"
              value={viewPolicy?.producerName || ''}
              testIdLabel="agency-label"
              testIdValue="agency-value"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Policy type"
              value={viewPolicy?.policyTypeEnumDescription || ''}
              testIdLabel="policy-type-label"
              testIdValue="policy-type-value"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Effective date"
              value={formatDate(viewPolicy?.policyEffectiveDate) || ''}
              testIdLabel="effective-date-label"
              testIdValue="effective-date-value"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Expiration date"
              value={formatDate(viewPolicy?.policyExpirationDate) || ''}
              testIdLabel="expiration-date-label"
              testIdValue="expiration-date-value"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <InfoBox
              label="Issue date"
              value={formatDate(viewPolicy?.policyIssueDate) || ''}
              testIdLabel="issue-date-label"
              testIdValue="issue-date-value"
            />
          </Grid>
        </Grid>
      </Box>

      <PolicyInformationInfoTable
        dataTestId="policy-state-table"
        title="Employer's primary state(s) of operation"
        columns={['State', 'Effective date', 'Expiration date', 'Premium']}
        rows={
          viewPolicy?.employerStatesOfOperation?.primaryStates?.map((primaryState, index) => ({
            key: `primary-${primaryState.stateCode || index}`,
            cells: [
              primaryState.stateName,
              formatDate(primaryState?.effectiveDate),
              formatDate(primaryState?.expirationDate),
              formatDollarAmount(primaryState.premium),
            ],
          })) || []
        }
      />

      <PolicyInformationInfoTable
        dataTestId="other-state-table"
        title="Employer's other state(s) of operation"
        columns={['State', 'Effective date', 'Expiration date', 'Premium']}
        rows={
          viewPolicy?.employerStatesOfOperation?.otherStates?.map((otherState, index) => ({
            key: `other-${otherState.stateCode || index}`,
            cells: [
              otherState.stateName,
              formatDate(otherState?.effectiveDate),
              formatDate(otherState?.expirationDate),
              formatDollarAmount(otherState.premium),
            ],
          })) || []
        }
      />

      <PolicyInformationInfoTable
        dataTestId="liability-limits-table"
        title="Employer's liability limits"
        columns={['Liability type', 'Limit']}
        rows={[
          {
            key: 'liability-accident',
            cells: [
              'Bodily injury by accident - Each accident',
              formatDollarAmount(viewPolicy?.liabilityLimits?.bodilyInjuryAccident),
            ],
          },
          {
            key: 'liability-policy',
            cells: [
              'Bodily injury by disease - Policy limit',
              formatDollarAmount(viewPolicy?.liabilityLimits?.bodilyInjuryDiseasePolicy),
            ],
          },
          {
            key: 'liability-employee',
            cells: [
              'Bodily injury by disease - Each employee',
              formatDollarAmount(viewPolicy?.liabilityLimits?.bodilyInjuryDiseaseEmployee),
            ],
          },
        ]}
      />
    </>
  );
}
